/**
 * spacesUpload.js
 * Upload a file to DigitalOcean Spaces using AWS Signature V4 directly
 * from the browser (no Node.js SDK required).
 *
 * Environment variables required:
 *   REACT_APP_DO_SPACES_ENDPOINT  e.g. https://sgp1.digitaloceanspaces.com
 *   REACT_APP_DO_SPACES_BUCKET    e.g. maami-media
 *   REACT_APP_DO_SPACES_ACCESS_KEY
 *   REACT_APP_DO_SPACES_SECRET_KEY
 */

const ENDPOINT = process.env.REACT_APP_DO_SPACES_ENDPOINT || '';
const BUCKET   = process.env.REACT_APP_DO_SPACES_BUCKET   || 'maami-media';
const ACCESS   = process.env.REACT_APP_DO_SPACES_ACCESS_KEY || '';
const SECRET   = process.env.REACT_APP_DO_SPACES_SECRET_KEY || '';

// Derive region from endpoint, e.g. "sgp1" from "https://sgp1.digitaloceanspaces.com"
function regionFromEndpoint(ep) {
  try {
    const host = new URL(ep).hostname; // sgp1.digitaloceanspaces.com
    return host.split('.')[0];         // sgp1
  } catch (_) {
    return 'sgp1';
  }
}

// ── Crypto helpers (Web Crypto API) ──────────────────────────────────────────

async function hmacSha256(key, data) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    typeof key === 'string' ? new TextEncoder().encode(key) : key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return crypto.subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(data));
}

async function sha256hex(data) {
  const buf = typeof data === 'string'
    ? new TextEncoder().encode(data)
    : data;
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function toHex(buf) {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function getDateStrings(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const y = date.getUTCFullYear();
  const mo = pad(date.getUTCMonth() + 1);
  const d = pad(date.getUTCDate());
  const h = pad(date.getUTCHours());
  const mi = pad(date.getUTCMinutes());
  const s = pad(date.getUTCSeconds());
  return {
    dateStamp: `${y}${mo}${d}`,
    amzDate: `${y}${mo}${d}T${h}${mi}${s}Z`,
  };
}

// ── Main upload function ──────────────────────────────────────────────────────

/**
 * Upload a File object to DigitalOcean Spaces.
 * @param {File}     file         - The file to upload
 * @param {string}   folder       - Folder prefix, e.g. "daily-reports"
 * @param {function} onProgress   - Optional callback(percent 0-100)
 * @returns {Promise<string>}     - Public URL of the uploaded file
 */
export async function uploadToSpaces(file, folder = 'daily-reports', onProgress) {
  if (!ENDPOINT || !ACCESS || !SECRET) {
    throw new Error('DigitalOcean Spaces credentials are not configured.');
  }

  const region = regionFromEndpoint(ENDPOINT);
  const service = 's3';

  // Build a unique key
  const ext = file.name.split('.').pop().toLowerCase();
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  const key = `${folder}/${ts}-${rand}.${ext}`;

  const now = new Date();
  const { dateStamp, amzDate } = getDateStrings(now);

  // Host for the bucket (path-style for Spaces)
  const host = new URL(ENDPOINT).hostname;
  const url = `${ENDPOINT}/${BUCKET}/${key}`;

  // Read file as ArrayBuffer for hashing
  const fileBuffer = await file.arrayBuffer();
  const payloadHash = await sha256hex(fileBuffer);

  // ── Canonical request ──────────────────────────────────────────────────────
  const canonicalHeaders =
    `host:${host}\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${amzDate}\n`;

  const signedHeaders = 'host;x-amz-content-sha256;x-amz-date';

  const canonicalRequest = [
    'PUT',
    `/${BUCKET}/${key}`,
    '',                   // no query string
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join('\n');

  // ── String to sign ─────────────────────────────────────────────────────────
  const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
  const canonicalHash = await sha256hex(canonicalRequest);

  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    credentialScope,
    canonicalHash,
  ].join('\n');

  // ── Signing key ────────────────────────────────────────────────────────────
  const kDate    = await hmacSha256(`AWS4${SECRET}`, dateStamp);
  const kRegion  = await hmacSha256(kDate, region);
  const kService = await hmacSha256(kRegion, service);
  const kSigning = await hmacSha256(kService, 'aws4_request');

  const signatureBuf = await hmacSha256(kSigning, stringToSign);
  const signature = toHex(signatureBuf);

  const authorization =
    `AWS4-HMAC-SHA256 Credential=${ACCESS}/${credentialScope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`;

  // ── PUT request ────────────────────────────────────────────────────────────
  // Use XMLHttpRequest for progress events
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Authorization', authorization);
    xhr.setRequestHeader('x-amz-date', amzDate);
    xhr.setRequestHeader('x-amz-content-sha256', payloadHash);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    // Make the object publicly readable
    xhr.setRequestHeader('x-amz-acl', 'public-read');

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(url);
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status} — ${xhr.responseText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(fileBuffer);
  });
}
